import { useEffect } from 'react';
import './App.css';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AddMemberToPartnerInput } from '@optimus/models';
import { unwrapResult } from '@reduxjs/toolkit';
import type { UpdateCompanyPartnerRequest } from './api/dashboardAPI';
import { NavigationRoutes } from './routes/navigationRoutes';
import { useAppDispatch, useAppSelector } from './store/hooks';
import {
  addAccessToken, addMemberId, addMemberPartnerRelationMemberEmail,
  addSolutionOrgId, addMemberPartnerRelationPartnerId, addMemberDeviceType,
  addMemberDeviceVersion,
  selectMemberInformation,
  selectMemberSession,
} from './store/slices/onboardingSlice';
import {
  HOME_DEPOT_NAME,
  JOIST_COMPLETE_REWARDS_EVERPRO_SIGNUP,
  JOIST_EVENT_BUFFER_RESPONSE,
  JOIST_MEMBER_PARTNER_RELATION_EMAIL_RESPONSE,
  JOIST_READY_FOR_EVENT_BUFFER_REQUEST,
  OPTIMUS_READY_FOR_EVENT_BUFFER_RESPONSE,
  REWARDS_CREDIT_CARD_COMPLETED,
  REWARDS_HOME_DEPOT_SIGN_IN_COMPLETED,
  REWARDS_PARTNER_CODE_COMPLETED,
} from './common/constants';
import { customHistory } from './common/utils/loginUtils';
import FaviconManager from './config/FaviconManager';
import { useSolutionOrg } from './config/useSolutionOrg';
import ScrollToTop from './common/ScrollToTop';
import { useBackToEdgeFromHomeDepot } from './common/useBackToEdgeFromHomeDepot';
import { updateCompanyPartner, updateMemberToPartner } from './store/thunks/dashboardThunk';
import { PartnerEnrollmentStatus } from './common/enums';
import { OptimusEvent } from './common/EventTrackingTypes';
import { useCustomEventTracking } from './common/useCustomEventTracking';
import { Logger } from './config/logging/logger';

function App() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { trackPartnerEnrollment } = useCustomEventTracking();
  const backToEdgeFromHomeDepot = useBackToEdgeFromHomeDepot();
  const { trackRequest } = useCustomEventTracking();
  const { company } = useAppSelector(selectMemberInformation);
  const memberSession = useAppSelector(selectMemberSession);
  const logger = Logger.create('App');

  // init custom history object to allow navigation from
  // anywhere in the react app (useful for navigation outside components)
  customHistory.navigate = useNavigate();
  customHistory.location = useLocation();

  const { isNativeAppExperience } = useSolutionOrg();

  useEffect(() => {
    const accessToken = searchParams.get('accessToken');
    const memberId = searchParams.get('memberId');
    const solutionOrgId = searchParams.get('solutionOrgId');
    const partnerId = searchParams.get('partnerId');
    // to enable passing the partnerMemberEmail as url query parameter for testing
    const partnerMemberEmail = searchParams.get('partnerMemberEmail');
    const memberDeviceType = searchParams.get('deviceType');
    const memberDeviceVersion = searchParams.get('deviceVersion');

    if (accessToken) {
      dispatch(addAccessToken(accessToken));
    }
    if (memberId) {
      dispatch(addMemberId(memberId));
    }
    if (solutionOrgId) {
      dispatch(addSolutionOrgId(solutionOrgId));
    }
    if (partnerId) {
      dispatch(addMemberPartnerRelationPartnerId(partnerId));
    }
    if (partnerMemberEmail) {
      dispatch(addMemberPartnerRelationMemberEmail(partnerMemberEmail));
    }
    if (memberDeviceType) {
      dispatch(addMemberDeviceType(memberDeviceType));
    }
    if (memberDeviceVersion) {
      dispatch(addMemberDeviceVersion(memberDeviceVersion));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // if this is an iframe
    if (isNativeAppExperience) {
      window.addEventListener('message', (event) => {
        const message = event?.data;
        if (message?.type === JOIST_MEMBER_PARTNER_RELATION_EMAIL_RESPONSE) {
          if (!(message.payload?.content)) {
            trackRequest('Error', 'Put', 'HomeDepotEmail', {
              error: { msg: 'missing home depot pro extra email' },
            });
          } else {
            const request: UpdateCompanyPartnerRequest = {
              partnerMemberEmail: message.payload.content,
              enrollmentStatus: PartnerEnrollmentStatus.InProgress,
            };

            dispatch(updateCompanyPartner({
              companyId: company?.id || '',
              partnerId: process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '',
              data: request,
            }))
              .then(unwrapResult)
              .catch((error: unknown) => {
                trackRequest('Error', 'Put', 'HomeDepotEmail', {
                  error,
                });
              });
            dispatch(addMemberPartnerRelationMemberEmail(message.payload?.content));
          }
        } else if (message?.type === JOIST_EVENT_BUFFER_RESPONSE) {
          const optimusEvents = message.payload?.content as OptimusEvent[];
          // check if partner code step is completed
          optimusEvents?.forEach((optimusEvent) => {
            if (optimusEvent.name === REWARDS_PARTNER_CODE_COMPLETED) {
              // update partnerCodeEnteredInMobile column of member partner relation table
              const request: AddMemberToPartnerInput = {
                partnerCodeEnteredInMobile: true,
                enrollmentStatus: PartnerEnrollmentStatus.InProgress,
              };

              dispatch(updateMemberToPartner({
                memberId: memberSession?.memberId,
                partnerId: process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '',
                data: request,
              }))
                .then(unwrapResult)
                .catch((error: unknown) => {
                  logger.error(Logger.format(error));
                });

              trackPartnerEnrollment('Click', 'MobileHomeDepotEnterCode', {
                ...optimusEvent.detail,
                isOptimizelyEvent: true,
              });

              trackPartnerEnrollment('Complete', 'HomeDepotSignup', {
                ...optimusEvent.detail,
                isOptimizelyEvent: true,
              });

              // fire event to indicate company submitted partner enrollment
              trackPartnerEnrollment('Submit', 'Form', {
                partnerName: HOME_DEPOT_NAME,
                enrollmentType: 'Unknown',
                isOptimizelyEvent: true,
              });
            }
            if (optimusEvent.name === REWARDS_CREDIT_CARD_COMPLETED) {
              trackPartnerEnrollment('Click', 'MobileHomeDepotEnterCreditCard', { ...optimusEvent.detail });
            }
            if (optimusEvent.name === REWARDS_HOME_DEPOT_SIGN_IN_COMPLETED) {
              trackPartnerEnrollment('Click', 'MobileHomeDepotSignIn', { ...optimusEvent.detail });
            }
          });
        } else if (message?.type === JOIST_READY_FOR_EVENT_BUFFER_REQUEST) {
          window.parent.postMessage({
            type: OPTIMUS_READY_FOR_EVENT_BUFFER_RESPONSE,
            payload: null,
          }, '*');
        }
      });

      // initiate handshake with injected user script in joist
      window.parent.postMessage({ type: 'OPTIMUS_EVENT_BUFFER_REQUEST', payload: null }, '*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleBackToEdgeFromHomeDepotMsg(event: MessageEvent) {
      if (event?.data?.type === JOIST_COMPLETE_REWARDS_EVERPRO_SIGNUP) {
        backToEdgeFromHomeDepot();
      }
    }

    if (isNativeAppExperience) {
      window.addEventListener('message', handleBackToEdgeFromHomeDepotMsg);
    }

    return () => {
      if (isNativeAppExperience) {
        window.removeEventListener('message', handleBackToEdgeFromHomeDepotMsg);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backToEdgeFromHomeDepot]);

  return (
    <>
      <ScrollToTop />
      <FaviconManager />
      <NavigationRoutes />
    </>
  );
}

export default App;
